import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Camera, CameraOff, Smartphone, ArrowRight, Building2, WifiOff } from "lucide-react";
import { ScanViewport } from "@/components/scan/ScanViewport";
import { ScanResultOverlay } from "@/components/scan/ScanResultOverlay";
import { ManualCodeForm } from "@/components/scan/ManualCodeForm";
import { KpiNumber } from "@/components/common/KpiNumber";
import { DoItSignature } from "@/components/brand/Brand";
import { UserChip } from "@/components/auth/UserChip";
import { useScanFlow } from "@/lib/demo/useScanFlow";
import { fullName, clockNow, fmt } from "@/lib/demo/utils";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n/i18n";
import { LangSwitcher } from "@/components/i18n/LangSwitcher";

const DURATION = 1800;

function toOverlay(r, t) {
  if (!r) return null;
  const p = r.participant;
  if (r.status === "error") return { tone: "error", icon: WifiOff, title: t("SIN CONEXIÓN"), note: t("No se ha podido registrar. Vuelve a escanear."), time: clockNow(), testId: "company-result-error" };
  if (r.status === "disabled") return { tone: "warning", icon: X, title: t("LECTURA DESACTIVADA"), note: t(r.message || ""), time: clockNow(), testId: "company-result-disabled" };
  if (r.status === "invalid") return { tone: "error", icon: X, title: t("QR NO VÁLIDO"), note: t("Código no reconocido"), time: clockNow(), testId: "company-result-invalid" };
  if (r.status === "duplicate") {
    return { tone: "warning", icon: Check, title: t(p.gender === "F" ? "✓ CANDIDATA YA REGISTRADA" : "✓ CANDIDATO YA REGISTRADO"), name: fullName(p), note: t("Currículum recibido anteriormente · {time}", { time: r.interaction.time }), testId: "company-result-duplicate" };
  }
  return { tone: "success", icon: Check, title: t("✓ CURRÍCULUM RECIBIDO"), name: fullName(p).toUpperCase(), lines: [t(p.titulacion), p.centro], time: r.interaction.time, note: t("Perfil añadido a tus candidatos"), testId: "company-result-received" };
}

const SidePanel = ({ summary, device, companyName }) => {
  const { t } = useI18n();
  return (
  <aside className="hidden w-[400px] space-y-5 pt-6 lg:block" data-testid="scanner-side-panel">
    <div className="flex items-center justify-between"><p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Scanner empresa")}</p><LangSwitcher testPrefix="scanner-lang" /></div>
    <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight">{t("ESCANEAR → RECIBIR CV → SIGUIENTE")}</h1>
    <p className="text-slate-600">{t("Sin formularios ni confirmaciones. Cada lectura comparte el perfil y el currículum con la empresa en ~1 segundo.")}</p>
    <div className="rounded-[24px] border border-slate-200 bg-white p-6">
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">{t("Un stand, varios reclutadores")}</p>
      <ul className="mt-4 space-y-2">
        {(summary?.devices || []).map((d) => (
          <li key={d.id} className={`flex items-center justify-between rounded-xl px-4 py-3 ${d.id === device ? "bg-upv-light" : "bg-slate-50"}`} data-testid={`scanner-device-row-${d.id}`}>
            <span className="flex min-w-0 items-center gap-3 text-sm font-semibold"><Smartphone className="h-4 w-4 shrink-0 text-upv" /><span className="truncate">{t(d.label)}</span>{d.id === device && <span className="rounded-full bg-upv px-2 py-0.5 text-[10px] text-white">{t("este")}</span>}</span>
            <span className="font-mono text-sm font-bold">{fmt(d.value)}</span>
          </li>
        ))}
      </ul>
      <Link to="/empresa/portal?s=candidatos" className="mt-4 flex items-center justify-between rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white" data-testid="scanner-portal-link">
        <span className="flex items-center gap-2"><Building2 className="h-4 w-4" /> {t("Todos alimentan el mismo portal {company}", { company: companyName })}</span><ArrowRight className="h-4 w-4" />
      </Link>
    </div>
    <div className="rounded-[24px] border border-slate-200 bg-white p-6">
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400">{t("Últimos candidatos recibidos")}</p>
      <ul className="mt-3 space-y-2">
        {(summary?.recent || []).map(({ interaction: i, participant: p }) => (
          <li key={i.id} className="flex items-center gap-3 text-sm" data-testid={`scanner-recent-${i.id}`}>
            <span className="font-mono font-bold">{i.time}</span><span className="truncate">{fullName(p)}</span><span className="ml-auto text-xs text-slate-400">{i.deviceId || "Web"}</span>
          </li>
        ))}
        {summary && summary.recent.length === 0 && <li className="text-sm text-slate-500">{t("Aún no hay candidatos.")}</li>}
      </ul>
    </div>
    <DoItSignature />
  </aside>
  );
};

export default function EmpresaScanner() {
  const { company, user, settings } = useAuth();
  const [camera, setCamera] = useState(false);
  const [summary, setSummary] = useState(null);
  const { t } = useI18n();
  const device = `RD-${String(user?.deviceSlot || 1).padStart(2, "0")}`;

  const load = useCallback(() => api.get("/company/summary").then((r) => setSummary(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, [load]);

  const process = useCallback(async (token, raw) => {
    try {
      const { data } = await api.post("/company/scan", { token: token || String(raw || "") });
      if (data.status !== "invalid") load();
      return data;
    } catch (e) {
      if (e?.response?.status === 403) return { status: "disabled", message: e.response.data?.detail };
      return { status: e?.response ? "invalid" : "error" };
    }
  }, [load]);
  const { phase, result, trigger } = useScanFlow(process, DURATION);

  const name = (company?.name || "").toUpperCase();

  return (
    <div className="min-h-screen bg-slate-100 lg:py-10" data-testid="company-scanner-page">
      <div className="mx-auto flex max-w-6xl justify-center gap-16">
        <div className={`relative flex min-h-[100dvh] w-full flex-col overflow-hidden bg-slate-950 text-white lg:h-[840px] lg:min-h-0 lg:w-[410px] lg:rounded-[48px] lg:border-[10px] lg:border-slate-900 lg:shadow-2xl pb-6`} data-testid="company-scanner-phone">
          <header className="px-6 pt-7">
            <div className="flex items-center justify-between gap-3">
              <p className="truncate font-display text-3xl font-extrabold tracking-tight" data-testid="company-scanner-name">{name}</p>
              <span className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[11px] font-semibold text-white/80" data-testid="company-scanner-device-button">
                <Smartphone className="h-3.5 w-3.5" /> Device {device.slice(3)}
              </span>
            </div>
            <div className="mt-0.5 flex items-center justify-between"><p className="text-sm text-white/50">Foro de Empleo UPV 2027</p><span className="lg:hidden"><UserChip dark /></span></div>
            <div className="mt-5 flex items-center gap-3 rounded-2xl bg-white/[0.06] px-4 py-3">
              <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" /><span className="relative h-2.5 w-2.5 rounded-full bg-emerald-400" /></span>
              <KpiNumber value={summary?.today || 0} testId="company-scanner-today-count" className="font-display text-2xl font-bold" />
              <span className="text-sm text-white/70">{t("candidatos recibidos hoy")}</span>
            </div>
          </header>
          <div className="flex flex-1 flex-col justify-center px-6 py-6">
            <ScanViewport phase={phase} cameraOn={camera} onDecode={(v) => trigger(v, { fromCamera: true })} />
            <p className="mt-6 text-center font-display text-xl font-extrabold tracking-tight" data-testid="company-scanner-instruction">{t("ESCANEA EL QR DEL CANDIDATO")}</p>
            <p className="mt-1.5 text-center text-sm text-white/55">{t("Recibe su perfil profesional y currículum al instante.")}</p>
            {!settings.optionA && <p className="mt-3 rounded-xl bg-amber-400/15 px-3 py-2 text-center text-xs font-semibold text-amber-200" data-testid="company-scanner-optiona-disabled">{t("La lectura de QR por la empresa está desactivada en esta edición")}</p>}
            {settings.optionB && <p className="mt-3 text-center text-xs text-white/45" data-testid="company-scanner-optionb-hint">{t("Recomendado: los candidatos escanean el QR de tu stand (Portal → Cuenta).")}</p>}
          </div>
          <div className="space-y-3 px-6">
            <button onClick={() => setCamera((c) => !c)} className="flex h-16 w-full items-center justify-center gap-2 rounded-2xl bg-upv font-display text-lg font-bold tracking-wider text-white hover:bg-[#a10f1a]" data-testid="company-scanner-camera-primary">
                {camera ? <CameraOff className="h-5 w-5" /> : <Camera className="h-5 w-5" />} {t(camera ? "Apagar cámara" : "Usar cámara")}
            </button>
            <ManualCodeForm dark onSubmit={(c) => trigger(c)} disabled={phase !== "idle"} testPrefix="company-scanner" />
          </div>
          <ScanResultOverlay data={phase === "result" ? toOverlay(result, t) : null} duration={DURATION} contained compact />
        </div>
        <SidePanel summary={summary} device={device} companyName={company?.name || ""} />
      </div>
    </div>
  );
}
